import React, { useState, useMemo } from "react";
import { COUNTRIES, Country } from "@/data/countries";
import { CountryFlag } from "../country-flag/country-flag";
import { StandardFrame } from "../frames/standard-frame";

interface CountryPickerProps {
  selectedCountry: string | null | undefined;
  onSelect: (countryCode: string) => void;
  onClose: () => void;
}

export const CountryPicker: React.FC<CountryPickerProps> = ({
  selectedCountry,
  onSelect,
  onClose,
}) => {
  const [searchQuery, setSearchQuery] = useState("");

  const filteredCountries = useMemo(() => {
    if (!searchQuery) return COUNTRIES;
    const lowerQuery = searchQuery.toLowerCase();
    return COUNTRIES.filter(
      (c) =>
        c.name.toLowerCase().includes(lowerQuery) ||
        c.code.toLowerCase().includes(lowerQuery),
    );
  }, [searchQuery]);

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 p-4">
      <StandardFrame
        variant="gray"
        className="w-full max-w-md max-h-[80vh] flex flex-col gap-4 p-4"
      >
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-bold text-pk-gray-5">Select Country</h2>
          <button
            onClick={onClose}
            className="text-pk-gray-4 hover:text-pk-gray-5 text-2xl font-bold"
          >
            ✕
          </button>
        </div>

        <input
          type="text"
          placeholder="Search country..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full px-4 py-2 rounded-lg border-2 border-pk-gray-3 bg-white text-pk-gray-5 placeholder-pk-gray-3 focus:border-pk-blue-4 outline-none transition-colors"
          autoFocus
        />

        <div className="flex-1 overflow-y-auto min-h-0 border-2 border-pk-gray-2 rounded-lg bg-white/50">
          {filteredCountries.length === 0 ? (
            <div className="p-8 text-center text-pk-gray-4">
              No countries found
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-1 p-2">
              {filteredCountries.map((country) => (
                <button
                  key={country.code}
                  onClick={() => onSelect(country.code)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                    selectedCountry === country.code
                      ? "bg-pk-blue-1 border-2 border-pk-blue-4"
                      : "hover:bg-white border-2 border-transparent hover:border-pk-gray-2"
                  }`}
                >
                  <CountryFlag countryCode={country.code} size="md" />
                  <span className="flex-1 text-left font-bold text-pk-gray-5">
                    {country.name}
                  </span>
                  {selectedCountry === country.code && (
                    <span className="text-pk-blue-5">✓</span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      </StandardFrame>
    </div>
  );
};
